import { IComboSlotOption, IItem, IItemOption } from '@rbi-ctg/menu';
import {
  IModifierSelectionActionWithData,
  IUserManualModifications,
  ModifierTypes,
  UserSelectionComboSlot,
  UserSelectionModifier,
  UserSelectionModifierMap,
} from 'state/product-wizard-v2/types';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';

import { IModifierData } from '../modifier/types';
import { getModifierType } from '../modifier/utils';
import { getModifiersActions } from '../utils';

import { ComboSlotType } from './ComboSlotType';
import {
  DefaultModifiersMap,
  GetModifiersWithSelectionsProps,
  GroupModifiersByDisplayNameFn,
  ModifiersPropsMap,
  ProductHierarchyType,
} from './types';

export const getModifierPropsSelections = (modifierSelections: UserSelectionModifierMap) => {
  return Object.values(modifierSelections ?? {}).reduce((acc, { modifier }) => {
    acc[modifier._key] = modifier;
    return acc;
  }, {} as IModifierData['selections']);
};

export const groupModifiersByDisplayName: GroupModifiersByDisplayNameFn = ({
  modifiers,
  modifiersMap,
  pickerAspectExclusions,
}): ModifiersPropsMap => {
  return modifiers.reduce((acc: ModifiersPropsMap, modifier: IItemOption) => {
    // Do not display modifier...

    // ...if it's excluded by pickerAspect mappings
    if (pickerAspectExclusions?.has(modifier._key)) {
      return acc;
    }

    // ...if there isn't a default selection for it
    const currentModifierSelection = modifiersMap[modifier._key];
    if (!currentModifierSelection) {
      return acc;
    }

    // ...if there are not enough modifier options to be selected
    // and the modifier type isn't either Single Choice or Multiple Choice.
    const modifierType = getModifierType(modifier);
    const isChoiceModifier = [ModifierTypes.MULTIPLE_CHOICE, ModifierTypes.SINGLE_CHOICE].includes(
      modifierType
    );
    const notEnoughModifierOptionsToSelect = modifier.options.length < 2;
    if (notEnoughModifierOptionsToSelect && !isChoiceModifier) {
      return acc;
    }

    const modifierProps = {
      modifier,
      selections: getModifierPropsSelections(currentModifierSelection),
    };

    const currentModifierSelectionValue = Object.values(currentModifierSelection)[0].modifier;

    const isZeroMultiplier = currentModifierSelectionValue.modifierMultiplier?.multiplier === 0;

    const displayGroupName = !isZeroMultiplier
      ? isChoiceModifier
        ? 'chooseItemAmount'
        : 'included'
      : 'addons';

    acc[displayGroupName] = [...(acc[displayGroupName] || []), modifierProps];
    return acc;
  }, {});
};

export const getModifiersDefaultOptions = (selectedItem: IItem): DefaultModifiersMap => {
  return Object.values(ProductWizardUtils.ComputeDefaultModifiersSelections(selectedItem)).reduce(
    (defaultsMap, modifierMap) => {
      Object.values(modifierMap).forEach(selectionAction => {
        defaultsMap[getModifierDefaultOptionKey(selectionAction)] =
          selectionAction.modifier.quantity;
      });
      return defaultsMap;
    },
    {} as DefaultModifiersMap
  );
};

export const getModifierDefaultOptionKey = (
  selectionAction: IModifierSelectionActionWithData
): string =>
  ProductWizardUtils.GetModifierOptionKey(
    selectionAction.option._key,
    selectionAction.modifier._key
  );

export const isDefaultSelectionAction = (
  defaultModifiersMap: DefaultModifiersMap,
  selAction: IModifierSelectionActionWithData
) =>
  defaultModifiersMap[getModifierDefaultOptionKey(selAction)]
    ? defaultModifiersMap[getModifierDefaultOptionKey(selAction)] === selAction.modifier.quantity
    : selAction.modifier.quantity === 0;

export const getUserModifications = (
  comboSlots: UserSelectionComboSlot,
  modifiers: UserSelectionModifier,
  defaultComboSlotSelections: UserSelectionComboSlot
): Omit<IUserManualModifications, 'pickerAspects'> => {
  const entries = comboSlots.entries();
  let entry = entries.next();
  const userModifications: Omit<IUserManualModifications, 'pickerAspects'> = {
    comboSlot: false,
    modifiers: false,
  };

  while (!entry.done) {
    const [key, value] = entry.value;

    if (value.selectedItem) {
      const defaultSlotItem = defaultComboSlotSelections.get(key)?.selectedItem;
      if (value.selectedItem._id !== defaultSlotItem?._id) {
        userModifications.comboSlot = true;
      }
      const defaultModifiersMap = getModifiersDefaultOptions(value.selectedItem);
      const hasModifiersChanges = getModifiersActions(modifiers[key]).some(
        selAction => !isDefaultSelectionAction(defaultModifiersMap, selAction)
      );

      if (hasModifiersChanges) {
        userModifications.modifiers = true;
      }

      if (userModifications.modifiers && userModifications.comboSlot) {
        return userModifications;
      }
    }
    entry = entries.next();
  }

  return userModifications;
};

export const getComboSlotType = (item: IItem | null): ComboSlotType | null => {
  if (!item?.productHierarchy) {
    return null;
  }

  const { productHierarchy } = item;

  if (productHierarchy.L1 === ProductHierarchyType.SIDES_OR_DRINKS) {
    if (productHierarchy.L2 === ProductHierarchyType.SIDES) {
      return ComboSlotType.SIDES;
    } else if (
      productHierarchy.L2 === ProductHierarchyType.DRINKS ||
      (productHierarchy.L2 === ProductHierarchyType.DESSERTS &&
        productHierarchy.L3 === ProductHierarchyType.SHAKES)
    ) {
      return ComboSlotType.DRINKS;
    }
  }

  return null;
};

export const getSelectedRowIndex = ({
  selectedItem,
  comboSlotOptions,
}: {
  selectedItem: IItem;
  comboSlotOptions: IComboSlotOption[];
}): number => {
  return comboSlotOptions.findIndex(option => option.option._id === selectedItem._id) || 0;
};

export const getRowIndex = (index: number, numColumns: number): number => {
  return Math.floor(index / numColumns) || 0;
};

export const getModifiersWithSelections = ({
  modifiers,
  modifiersMap,
  pickerAspectExclusions,
}: GetModifiersWithSelectionsProps): IModifierData[] => {
  // @TODO: we should consolidate this logic with groupModifiersByDisplayName function
  return modifiers.reduce((acc: IModifierData[], modifier: IItemOption) => {
    // Do not display modifier...

    // ...if it's excluded by pickerAspect mappings
    if (pickerAspectExclusions?.has(modifier._key)) {
      return acc;
    }

    // ...if there isn't a default selection for it
    const currentModifierSelection = modifiersMap[modifier._key];

    if (!currentModifierSelection) {
      return acc;
    }

    // ...if there are not enough modifier options to be selected
    // and the modifier type isn't either Single Choice or Multiple Choice.
    const modifierType = getModifierType(modifier);
    const notEnoughModifierOptionsToSelect = modifier.options.length < 2;
    if (
      notEnoughModifierOptionsToSelect &&
      ![ModifierTypes.MULTIPLE_CHOICE, ModifierTypes.SINGLE_CHOICE].includes(modifierType)
    ) {
      return acc;
    }

    const modifierData = {
      modifier,
      selections: getModifierPropsSelections(currentModifierSelection),
    };

    return [...acc, modifierData];
  }, []);
};
